import React from 'react';
import { startOfDay, isAfter, isBefore, endOfToday, endOfDay } from 'date-fns';
import styled from 'styled-components';
import { AuthContext } from 'contexts';
import { useBreakpoint } from 'hooks';
import { Button, DatePicker, Input } from 'components';
import { MessageSearchState, SelectedRep } from './hooks';
import MessageExportButton from './MessageExportButton';
import MessageUserSearch from './MessageUserSearch';

interface MessageSearchBarProps {
  searchState: MessageSearchState;
  isValidExport: boolean;
  userInfo?: SelectedRep;
  getConvos: (page?: number) => Promise<void>;
  setSearchState: React.Dispatch<React.SetStateAction<MessageSearchState>>;
}

const MessageSearchBar: React.FC<MessageSearchBarProps> = ({
  searchState,
  isValidExport,
  userInfo,
  getConvos,
  setSearchState,
}) => {
  const { lg } = useBreakpoint();
  const { auth } = React.useContext(AuthContext);

  const exportPath = React.useMemo(() => {
    const { text, dateFrom, dateTo, user } = searchState;
    let path = '/messaging/report/downloadPDF';
    const searchParams: Record<string, string> = {};
    if (text) searchParams.search = text;
    if (dateFrom) searchParams.dateFrom = dateFrom.toISOString();
    if (dateTo) searchParams.dateTo = dateTo.toISOString();
    if (userInfo || user) {
      // Prioritize userInfo first
      const userId = userInfo ? userInfo.id : user!.id;
      let userName = '';

      if (userInfo) {
        userName = userInfo.firstName;
        if (userInfo.lastName) userName += ` ${userInfo.lastName}`;
      } else {
        userName = user!.firstName;
        if (user!.lastName) userName += ` ${user!.lastName}`;
      }

      searchParams.searchUser = userId;
      searchParams.searchUserName = userName;
    }
    const searchPath = new URLSearchParams(searchParams).toString();
    if (Object.keys(searchParams).length) {
      path += `?${searchPath}`;
    }
    return path;
  }, [searchState, userInfo]);

  React.useEffect(() => {
    getConvos();
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    getConvos();
  };

  return (
    <StyledMessagingSearch
      $isMobile={!lg}
      $isAdmin={auth.isAdmin}
      onSubmit={handleSubmit}
    >
      <StyledInputGroup>
        <Input
          label="Search"
          value={searchState.text}
          onChange={({ target: { value } }) =>
            setSearchState(prev => ({ ...prev, text: value }))
          }
        />
        <Button type="primary" htmlType="submit">
          Search
        </Button>
      </StyledInputGroup>
      <DatePicker
        label="Date From"
        value={searchState.dateFrom}
        onChange={value =>
          setSearchState(prev => ({
            ...prev,
            dateFrom: value && startOfDay(value),
          }))
        }
        disabledDate={date => {
          const { dateTo } = searchState;
          const endDate = endOfDay(dateTo) ?? endOfToday();
          return isAfter(startOfDay(date), endDate);
        }}
        format="MMM D, YYYY"
        allowClear
      />
      <DatePicker
        label="Date To"
        value={searchState.dateTo}
        onChange={value =>
          setSearchState(prev => ({
            ...prev,
            dateTo: value && endOfDay(value),
          }))
        }
        disabledDate={dateTo => {
          if (searchState.dateFrom) {
            return isBefore(endOfDay(dateTo), startOfDay(searchState.dateFrom));
          }
          return isAfter(endOfDay(dateTo), endOfToday());
        }}
        format="MMM D, YYYY"
        allowClear
      />
      {auth.isAdmin && (
        <MessageUserSearch
          searchState={searchState}
          userInfo={userInfo}
          setSearchState={setSearchState}
        />
      )}
      <MessageExportButton
        isValidExport={isValidExport}
        exportPath={exportPath}
      />
    </StyledMessagingSearch>
  );
};

export default MessageSearchBar;

const StyledMessagingSearch = styled.form<{
  $isMobile?: boolean;
  $isAdmin?: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ $isMobile, $isAdmin }) =>
    $isMobile ? '1fr' : `2fr repeat(${$isAdmin ? '4' : '3'}, 1fr)`};
  align-items: end;
  gap: 20px 30px;
  margin-bottom: 30px;

  .ant-list-item {
    padding: 0;
  }
`;

const StyledInputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 75px;
  align-items: end;
  gap: 15px;
`;
